import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ChevronUp, Loader2, Ticket } from "lucide-react";
import {
  fetchShowcaseBets,
  selectShowcaseBets,
  selectBetHouseLoading,
} from "../../redux/slices/betHouseSlice";
import { patchAppUser, selectAppUser } from "../redux/appAuthSlice";
import {
  addSelectionToSlip,
  clearBettingDesk,
  fetchBettingSlip,
  placeBetTicket,
  removeSelectionFromSlip,
} from "../redux/bettingDeskSlice";
import {
  countLiveFixtures,
  sortFixturesForSchedule,
} from "../utils/betDetailHelpers";
import { isHouseOperator } from "../utils/houseAccess";
import AppBackControl from "../components/ui/AppBackControl";
import BetChatModal from "../components/bondhouse/BetChatModal";
import BetSlipPanel from "../components/place-bet/BetSlipPanel";
import FixtureProgramCard from "../components/place-bet/FixtureProgramCard";
import OddsMarketDrawer from "../components/place-bet/OddsMarketDrawer";
import PlaceBetDeskChrome from "../components/place-bet/PlaceBetDeskChrome";
import PlaceBetStakeModal from "../components/place-bet/PlaceBetStakeModal";
import ProgramFilters from "../components/place-bet/ProgramFilters";
import ProgramFiltersRail from "../components/place-bet/ProgramFiltersRail";
import {
  combinedOdds,
  computeDeepMarketsThreshold,
  countDeepBoardFixtures,
  countFixturesByTimeFilter,
  countFixturesWithBettors,
  extractFixtureLeagues,
  filterBettableFixtures,
  filterProgramFixtures,
  findSlipLeg,
  fmtOdd,
  groupStartingSoonFixtures,
  hasActiveProgramFilters,
  isSameOddPick,
  minSelectionsFor,
  PLACE_BET_PATH,
} from "../utils/bettingDeskHelpers";
import "../place-bet-desk.css";

const PlaceBetDesk = () => {
  const { houseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkFixtureId = searchParams.get("fixture");

  const user = useSelector(selectAppUser);
  const userId = user?._id;
  const userCash = Number(user?.CASH ?? 0) || 0;

  const houses = useSelector(selectShowcaseBets);
  const isLoadingHouses = useSelector(selectBetHouseLoading);
  const { slip, status, actionStatus, error, actionError } = useSelector(
    (s) => s.bettingDesk
  );

  const house = useMemo(
    () => houses.find((h) => h?._id === houseId) || null,
    [houses, houseId]
  );

  const canManage = useMemo(
    () => isHouseOperator(house, userId),
    [house, userId]
  );

  const fixtures = useMemo(
    () => sortFixturesForSchedule((house?.fixtures || []).filter((f) => f && f._id)),
    [house]
  );

  const bettable = useMemo(() => filterBettableFixtures(fixtures), [fixtures]);
  const liveCount = useMemo(() => countLiveFixtures(fixtures), [fixtures]);

  const [search, setSearch] = useState("");
  const [timeFilter, setTimeFilter] = useState("all");
  const [leagueId, setLeagueId] = useState("");
  const [onSlipOnly, setOnSlipOnly] = useState(false);
  const [startingSoon, setStartingSoon] = useState(false);
  const [popularOnly, setPopularOnly] = useState(false);
  const [deepBoardOnly, setDeepBoardOnly] = useState(false);
  const [sort, setSort] = useState("kickoff");
  const [marketFixture, setMarketFixture] = useState(null);
  const [stakeOpen, setStakeOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const busy = actionStatus === "loading";

  const slipLegCount = slip?.fixtures?.length || 0;
  const totalOdds = useMemo(() => combinedOdds(slip), [slip]);
  const minLegs = minSelectionsFor(house);
  const legsReady = slipLegCount >= minLegs;

  const leagues = useMemo(() => extractFixtureLeagues(bettable), [bettable]);

  const searchScopedFixtures = useMemo(
    () =>
      filterProgramFixtures(bettable, {
        search,
        leagueId: "",
        timeFilter: "all",
        onSlipOnly: false,
        startingSoon: false,
        sort: "kickoff",
        slip,
      }),
    [bettable, search, slip]
  );

  const timeCounts = useMemo(
    () => countFixturesByTimeFilter(searchScopedFixtures),
    [searchScopedFixtures]
  );

  const deepMarketsThreshold = useMemo(
    () => computeDeepMarketsThreshold(searchScopedFixtures),
    [searchScopedFixtures]
  );

  const filteredFixtures = useMemo(
    () =>
      filterProgramFixtures(bettable, {
        search,
        leagueId,
        timeFilter,
        onSlipOnly,
        startingSoon,
        popularOnly,
        deepBoardOnly,
        deepMarketsThreshold,
        sort,
        slip,
      }),
    [
      bettable,
      search,
      leagueId,
      timeFilter,
      onSlipOnly,
      startingSoon,
      popularOnly,
      deepBoardOnly,
      deepMarketsThreshold,
      sort,
      slip,
    ]
  );

  const filtersActive = useMemo(
    () =>
      hasActiveProgramFilters({
        search,
        leagueId,
        timeFilter,
        onSlipOnly,
        startingSoon,
        popularOnly,
        deepBoardOnly,
        sort,
      }),
    [search, leagueId, timeFilter, onSlipOnly, startingSoon, popularOnly, deepBoardOnly, sort]
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (timeFilter !== "all") n += 1;
    if (leagueId) n += 1;
    if (onSlipOnly) n += 1;
    if (startingSoon) n += 1;
    if (popularOnly) n += 1;
    if (deepBoardOnly) n += 1;
    if (sort !== "kickoff") n += 1;
    return n;
  }, [timeFilter, leagueId, onSlipOnly, startingSoon, popularOnly, deepBoardOnly, sort]);

  const startingSoonGroups = useMemo(
    () => groupStartingSoonFixtures(bettable),
    [bettable]
  );

  const startingSoonCount = useMemo(
    () => startingSoonGroups.reduce((sum, group) => sum + group.fixtures.length, 0),
    [startingSoonGroups]
  );

  const popularCount = useMemo(
    () => countFixturesWithBettors(searchScopedFixtures),
    [searchScopedFixtures]
  );

  const deepBoardCount = useMemo(
    () => countDeepBoardFixtures(searchScopedFixtures, deepMarketsThreshold),
    [searchScopedFixtures, deepMarketsThreshold]
  );

  const handleClearFilters = useCallback(() => {
    setSearch("");
    setTimeFilter("all");
    setLeagueId("");
    setOnSlipOnly(false);
    setStartingSoon(false);
    setPopularOnly(false);
    setDeepBoardOnly(false);
    setSort("kickoff");
  }, []);

  useEffect(() => {
    if (!houses.length) dispatch(fetchShowcaseBets({ page: 1, limit: 30 }));
  }, [houses.length, dispatch]);

  useEffect(() => {
    if (!userId || !houseId) return;
    dispatch(fetchBettingSlip({ userId, betId: houseId }));
    return () => {
      dispatch(clearBettingDesk());
    };
  }, [userId, houseId, dispatch]);

  useEffect(() => {
    setSearch("");
    setTimeFilter("all");
    setLeagueId("");
    setOnSlipOnly(false);
    setStartingSoon(false);
    setPopularOnly(false);
    setDeepBoardOnly(false);
    setSort("kickoff");
    setSlipOpen(false);
    setFiltersOpen(false);
  }, [houseId]);

  useEffect(() => {
    if (!leagueId) return;
    if (!leagues.some((l) => l.id === leagueId)) setLeagueId("");
  }, [leagueId, leagues]);

  useEffect(() => {
    if (canManage && houseId) {
      navigate(`/app/bethouse/${houseId}/manage`, { replace: true });
    }
  }, [canManage, houseId, navigate]);

  useEffect(() => {
    if (slipLegCount === 0) {
      setSlipOpen(false);
      setOnSlipOnly(false);
    }
  }, [slipLegCount]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!deepLinkFixtureId || !bettable.length) return;

    const fixture =
      bettable.find((fx) => String(fx._id) === String(deepLinkFixtureId)) || null;
    if (fixture) {
      setMarketFixture(fixture);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("fixture");
    setSearchParams(nextParams, { replace: true });
  }, [deepLinkFixtureId, bettable, searchParams, setSearchParams]);

  const handleViewHouse = useCallback(() => {
    if (houseId) navigate(`/app/bethouse/${houseId}`);
  }, [navigate, houseId]);

  const handlePick = useCallback(
    async (fixture, selectedOdd) => {
      if (!userId || !houseId || !fixture?._id || !selectedOdd) return false;
      const existing = findSlipLeg(slip, fixture._id);
      try {
        if (existing && isSameOddPick(existing.selectedOdd, selectedOdd)) {
          await dispatch(
            removeSelectionFromSlip({ userId, betId: houseId, fixtureId: fixture._id })
          ).unwrap();
          return true;
        }
        if (existing) {
          await dispatch(
            removeSelectionFromSlip({ userId, betId: houseId, fixtureId: fixture._id })
          ).unwrap();
        }
        await dispatch(
          addSelectionToSlip({
            userId,
            betId: houseId,
            fixtureId: fixture._id,
            selectedOdd,
          })
        ).unwrap();
        if (
          typeof window !== "undefined" &&
          window.matchMedia("(max-width: 880px)").matches
        ) {
          setSlipOpen(true);
        }
        return true;
      } catch (err) {
        setToast(typeof err === "string" ? err : "Could not update slip");
        return false;
      }
    },
    [dispatch, userId, houseId, slip]
  );

  const handleQuickPick = useCallback(
    (fixture) => async (pick) => {
      await handlePick(fixture, pick);
    },
    [handlePick]
  );

  const handleRemove = useCallback(
    async (fixtureId) => {
      if (!userId || !houseId) return;
      try {
        await dispatch(
          removeSelectionFromSlip({ userId, betId: houseId, fixtureId })
        ).unwrap();
      } catch (err) {
        setToast(typeof err === "string" ? err : "Could not remove leg");
      }
    },
    [dispatch, userId, houseId]
  );

  const handlePlace = useCallback(
    async (amountBet) => {
      if (!userId || !houseId) return;
      try {
        const placed = await dispatch(
          placeBetTicket({ userId, betId: houseId, amountBet, cashType: "Cash" })
        ).unwrap();
        setStakeOpen(false);
        setSlipOpen(false);
        dispatch(patchAppUser({ CASH: Math.max(0, userCash - amountBet) }));
        setToast("Ticket placed — good luck!");
        if (placed?._id) {
          navigate(`/app/joined-bets/${placed._id}`);
        }
      } catch (err) {
        /* error shown in modal */
      }
    },
    [dispatch, userId, houseId, userCash, navigate]
  );

  const showLoading = (isLoadingHouses && !house) || (status === "loading" && !slip && userId);
  const houseClosed = house && house.status !== "active";
  const noBettable = house && bettable.length === 0;
  const hasSlip = slipLegCount > 0;

  if (showLoading) {
    return (
      <div className="pbd-shell">
        <div className="pbd-loading">
          <Loader2 size={20} className="animate-spin" />
          <span>Opening betting desk…</span>
        </div>
      </div>
    );
  }

  if (!house) {
    return (
      <div className="pbd-shell">
        <header className="pbd-chrome">
          <AppBackControl onClick={() => navigate("/app/bethouse")} />
          <h1>House unavailable</h1>
        </header>
        <div className="pbd-empty">
          <p>This house isn&apos;t on the floor right now.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pbd-shell">
      <div className="pbd-ambient" aria-hidden>
        <span className="pbd-ambient-a" />
        <span className="pbd-ambient-b" />
      </div>

      <div className="pbd-desk-top">
        <PlaceBetDeskChrome
          houseName={house.name}
          liveCount={liveCount}
          startingSoonGroups={startingSoonGroups}
          startingSoon={startingSoon}
          onStartingSoonToggle={() => setStartingSoon((v) => !v)}
          openCount={bettable.length}
          slipCount={slipLegCount}
          combinedOdds={totalOdds}
          onViewHouse={handleViewHouse}
          onChat={() => setChatOpen(true)}
          filtersOpen={filtersOpen}
          onFiltersToggle={() => setFiltersOpen((v) => !v)}
          activeFilterCount={activeFilterCount}
        >
          <ProgramFilters
            variant="search"
            search={search}
            onSearchChange={setSearch}
          />
        </PlaceBetDeskChrome>
      </div>

      <div className="pbd-desk">
        {(houseClosed || noBettable || error) && (
          <div className="pbd-banner">
            {houseClosed ? "This house is not accepting new tickets." : null}
            {!houseClosed && noBettable ? "No upcoming fixtures open for betting." : null}
            {error ? String(error) : null}
          </div>
        )}

        <div className="pbd-desk-body">
          <div className="pbd-desk-sidebar" aria-label="Program filters">
            <ProgramFiltersRail
              timeFilter={timeFilter}
              onTimeFilterChange={setTimeFilter}
              leagueId={leagueId}
              onLeagueChange={setLeagueId}
              leagues={leagues}
              timeCounts={timeCounts}
              onSlipOnly={onSlipOnly}
              onSlipOnlyChange={setOnSlipOnly}
              slipCount={slipLegCount}
              startingSoon={startingSoon}
              onStartingSoonChange={setStartingSoon}
              startingSoonCount={startingSoonCount}
              popularOnly={popularOnly}
              onPopularOnlyChange={setPopularOnly}
              popularCount={popularCount}
              deepBoardOnly={deepBoardOnly}
              onDeepBoardOnlyChange={setDeepBoardOnly}
              deepBoardCount={deepBoardCount}
              deepMarketsThreshold={deepMarketsThreshold}
              sort={sort}
              onSortChange={setSort}
              onClear={handleClearFilters}
              activeCount={activeFilterCount}
              resultCount={filteredFixtures.length}
            />
          </div>

          <div className="pbd-desk-main">
            <div className={`pbd-body ${hasSlip ? "pbd-body-has-slip" : ""}`}>
              <section className="pbd-program">
                <div className="pbd-program-grid">
                  {filteredFixtures.length === 0 ? (
                    <div className="pbd-program-empty">
                      <p>
                        {filtersActive
                          ? "No open fixtures match your filters."
                          : "No open fixtures match your search."}
                      </p>
                      {filtersActive ? (
                        <button type="button" className="pbd-program-empty-btn" onClick={handleClearFilters}>
                          Reset filters
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    filteredFixtures.map((fixture) => (
                      <FixtureProgramCard
                        key={fixture._id}
                        fixture={fixture}
                        slip={slip}
                        busy={busy}
                        onQuickPick={handleQuickPick(fixture)}
                        onOpenMarkets={setMarketFixture}
                      />
                    ))
                  )}
                </div>
              </section>

              {hasSlip ? (
                <div className={`pbd-slip-wrap ${slipOpen ? "pbd-slip-wrap-open" : ""}`}>
                  <BetSlipPanel
                    house={house}
                    slip={slip}
                    userCash={userCash}
                    busy={busy}
                    onRemove={handleRemove}
                    onPlaceClick={() => setStakeOpen(true)}
                    onClose={() => setSlipOpen(false)}
                    showClose={slipOpen}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {filtersOpen ? (
        <>
          <button
            type="button"
            className="pbd-filters-drawer-backdrop"
            aria-label="Close filters"
            onClick={() => setFiltersOpen(false)}
          />
          <div className="pbd-filters-drawer" role="dialog" aria-label="Program filters">
            <ProgramFiltersRail
              compact
              onClose={() => setFiltersOpen(false)}
              timeFilter={timeFilter}
              onTimeFilterChange={setTimeFilter}
              leagueId={leagueId}
              onLeagueChange={setLeagueId}
              leagues={leagues}
              timeCounts={timeCounts}
              onSlipOnly={onSlipOnly}
              onSlipOnlyChange={setOnSlipOnly}
              slipCount={slipLegCount}
              startingSoon={startingSoon}
              onStartingSoonChange={setStartingSoon}
              startingSoonCount={startingSoonCount}
              popularOnly={popularOnly}
              onPopularOnlyChange={setPopularOnly}
              popularCount={popularCount}
              deepBoardOnly={deepBoardOnly}
              onDeepBoardOnlyChange={setDeepBoardOnly}
              deepBoardCount={deepBoardCount}
              deepMarketsThreshold={deepMarketsThreshold}
              sort={sort}
              onSortChange={setSort}
              onClear={handleClearFilters}
              activeCount={activeFilterCount}
              resultCount={filteredFixtures.length}
            />
          </div>
        </>
      ) : null}

      {hasSlip && !slipOpen ? (
        <div className="pbd-slip-bar">
          <button
            type="button"
            className="pbd-slip-bar-open"
            onClick={() => setSlipOpen(true)}
            aria-label="Open bet slip"
          >
            <Ticket size={14} />
            <span className="pbd-slip-bar-copy">
              Slip · {slipLegCount}/{minLegs}
            </span>
            <strong className="pbd-slip-bar-odds tabular-nums">
              {fmtOdd(totalOdds)}
            </strong>
            <ChevronUp size={14} className="pbd-slip-bar-chevron" aria-hidden />
          </button>
          <button
            type="button"
            className={`pbd-slip-bar-cta ${
              legsReady ? "pbd-slip-bar-cta-ready" : ""
            }`}
            onClick={() =>
              legsReady ? setStakeOpen(true) : setSlipOpen(true)
            }
          >
            {legsReady
              ? "Place bet"
              : `+${minLegs - slipLegCount} leg${minLegs - slipLegCount === 1 ? "" : "s"}`}
          </button>
        </div>
      ) : null}

      <OddsMarketDrawer
        open={Boolean(marketFixture)}
        fixture={marketFixture}
        currentPick={marketFixture ? findSlipLeg(slip, marketFixture._id)?.selectedOdd : null}
        busy={busy}
        onClose={() => setMarketFixture(null)}
        onSelect={(pick) => handlePick(marketFixture, pick)}
      />

      <PlaceBetStakeModal
        open={stakeOpen}
        house={house}
        slip={slip}
        userCash={userCash}
        busy={busy}
        error={actionError}
        onClose={() => setStakeOpen(false)}
        onConfirm={handlePlace}
      />

      <BetChatModal open={chatOpen} onClose={() => setChatOpen(false)} house={house} />

      {toast ? (
        <div className="pbd-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
};

export { PLACE_BET_PATH };
export default PlaceBetDesk;
